/**
 * My Replays history page with filters and improvements
 */
import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Play, Download, Share2, Clock, Zap, 
  Calendar, MapPin, Loader2, X, Copy, Check,
  Filter, Search, RefreshCw, MessageCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Navbar } from '../components/Navbar';
import CompleteProfileModal from '../components/CompleteProfileModal';
import ExtendStorageModal from '../components/ExtendStorageModal';
import { clipsAPI, shareAPI } from '../services/api';
import { useClipsStore, useSessionStore, useAuthStore } from '../store';
import { toast } from 'sonner';

export default function Replays() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const highlightClipId = searchParams.get('clip');
  
  // Use clips store for caching
  const { 
    clips: cachedClips, 
    setClips: setCachedClips, 
    isCacheValid,
    isLoading: storeLoading,
    setLoading: setStoreLoading 
  } = useClipsStore();
  const { club, court } = useSessionStore();
  const { user } = useAuthStore();
  const canPlayInPlatform = Boolean(user?.profile_completed);
  // Email verification only affects storage duration (24h vs 72h), not sharing/downloading
  const canShareOrDownload = true;
  
  const [loading, setLoading] = useState(false);
  const [clips, setClips] = useState(cachedClips);
  const [selectedClip, setSelectedClip] = useState(null);
  const [shareUrl, setShareUrl] = useState('');
  const [shareLoading, setShareLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  
  // Video player state
  const [showVideoPlayer, setShowVideoPlayer] = useState(false);
  const [videoUrl, setVideoUrl] = useState('');
  const [videoLoading, setVideoLoading] = useState(false);
  const [playingClip, setPlayingClip] = useState(null);
  
  // Modals state
  const [showCompleteProfile, setShowCompleteProfile] = useState(false);
  const [showExtendStorage, setShowExtendStorage] = useState(false);
  const [pendingAction, setPendingAction] = useState(null); // { type: 'play'|'share'|'download', clip: ... }
  
  // Filters
  const [selectedType, setSelectedType] = useState('all'); // all, normal, fast
  const [selectedDate, setSelectedDate] = useState('all'); // all, today, yesterday
  const [searchQuery, setSearchQuery] = useState('');
  
  // Session key for cache invalidation
  const sessionKey = club && court ? `${club.id || club}-${court.id || court}` : null;
  
  useEffect(() => {
    // Check if we have valid cache
    if (isCacheValid(sessionKey) && cachedClips.length > 0) {
      setClips(cachedClips);
      setLoading(false);
      return;
    }
    
    // Otherwise fetch fresh data
    fetchClips();
  }, [sessionKey]); // fetchClips, isCacheValid, cachedClips are stable or handled internally
  
  useEffect(() => {
    // Auto-select highlighted clip
    if (highlightClipId && clips.length > 0) {
      const clip = clips.flatMap(g => g.clips).find(c => c.id === highlightClipId);
      if (clip) {
        setSelectedClip(clip);
      }
    }
  }, [highlightClipId, clips]);
  
  const fetchClips = async (forceRefresh = false) => {
    // Skip if cache is valid and not forcing refresh
    if (!forceRefresh && isCacheValid(sessionKey) && cachedClips.length > 0) {
      setClips(cachedClips);
      return;
    }
    
    try {
      setLoading(true);
      const response = await clipsAPI.listClips();
      const newClips = response.data.clips || [];
      
      setClips(newClips);
      setCachedClips(newClips, sessionKey);
    } catch (err) {
      console.error('Failed to fetch clips:', err);
      toast.error('Error cargando replays');
    } finally {
      setLoading(false);
    }
  };
  
  // Load video URL when clip is selected
  useEffect(() => {
    const loadVideoUrl = async () => {
      if (!selectedClip || selectedClip.status !== 'ready') {
        setVideoUrl('');
        return;
      }
      
      setVideoLoading(true);
      try {
        const response = await clipsAPI.getStreamUrl(selectedClip.id);
        const url = response.data?.stream_url;
        if (url) {
          setVideoUrl(url);
        } else {
          setVideoUrl('');
        }
      } catch (err) {
        console.error('Error loading video:', err);
        setVideoUrl('');
      } finally {
        setVideoLoading(false);
      }
    };
    
    loadVideoUrl();
  }, [selectedClip]);
  
  // Filter and sort clips
  const filteredClips = useMemo(() => {
    let result = clips;
    
    // Filter by type (normal/fast)
    if (selectedType !== 'all') {
      result = result.map(group => ({
        ...group,
        clips: group.clips.filter(clip => 
          selectedType === 'fast' ? clip.is_fast : !clip.is_fast
        )
      })).filter(group => group.clips.length > 0);
    }
    
    // Filter by date (today/yesterday)
    // Use local timezone for comparison
    if (selectedDate !== 'all') {
      const now = new Date();
      // Get today's date in local timezone as YYYY-MM-DD
      const todayLocal = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const todayStr = todayLocal.toISOString().split('T')[0];
      
      // Get yesterday's date in local timezone
      const yesterdayLocal = new Date(todayLocal);
      yesterdayLocal.setDate(yesterdayLocal.getDate() - 1);
      const yesterdayStr = yesterdayLocal.toISOString().split('T')[0];
      
      result = result.filter(group => {
        // The group.date comes from backend in UTC as YYYY-MM-DD
        // We need to check if any clip in the group was created today/yesterday in local time
        const hasMatchingClip = group.clips.some(clip => {
          const clipDate = new Date(clip.created_at);
          const clipLocalDate = new Date(clipDate.getFullYear(), clipDate.getMonth(), clipDate.getDate());
          const clipDateStr = clipLocalDate.toISOString().split('T')[0];
          
          if (selectedDate === 'today') {
            return clipDateStr === todayStr;
          } else if (selectedDate === 'yesterday') {
            return clipDateStr === yesterdayStr;
          }
          return true;
        });
        
        return hasMatchingClip;
      }).map(group => {
        // Also filter clips within the group
        const now = new Date();
        const todayLocal = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const todayStr = todayLocal.toISOString().split('T')[0];
        const yesterdayLocal = new Date(todayLocal);
        yesterdayLocal.setDate(yesterdayLocal.getDate() - 1);
        const yesterdayStr = yesterdayLocal.toISOString().split('T')[0];
        
        return {
          ...group,
          clips: group.clips.filter(clip => {
            const clipDate = new Date(clip.created_at);
            const clipLocalDate = new Date(clipDate.getFullYear(), clipDate.getMonth(), clipDate.getDate());
            const clipDateStr = clipLocalDate.toISOString().split('T')[0];
            
            if (selectedDate === 'today') {
              return clipDateStr === todayStr;
            } else if (selectedDate === 'yesterday') {
              return clipDateStr === yesterdayStr;
            }
            return true;
          })
        };
      }).filter(group => group.clips.length > 0);
    }
    
    // Search by club name
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(group => 
        group.club_name?.toLowerCase().includes(query)
      );
    }
    
    return result;
  }, [clips, selectedType, selectedDate, searchQuery]);
  
  // Stats
  const stats = useMemo(() => {
    const allClips = clips.flatMap(g => g.clips);
    return {
      total: allClips.length,
      normal: allClips.filter(c => !c.is_fast).length,
      fast: allClips.filter(c => c.is_fast).length,
      ready: allClips.filter(c => c.status === 'ready').length,
    };
  }, [clips]);
  
  const handleShare = async (clip) => {
    if (!canShareOrDownload) {
      // Close detail dialog first to avoid Radix pointer-events conflict
      setSelectedClip(null);
      // Show extend storage modal
      setPendingAction({ type: 'share', clip });
      setShowExtendStorage(true);
      return;
    }

    await shareClipDirectly(clip);
  };
  
  const shareClipDirectly = async (clip) => {
    setShareLoading(true);
    setShowShareDialog(true);
    
    try {
      const response = await clipsAPI.createShortUrl(clip.id);
      setShareUrl(response.data.full_url);
    } catch (err) {
      console.error('Short URL error:', err);
      try {
        const fallbackResponse = await shareAPI.createShareLink({
          clip_id: clip.id,
          expires_hours: 24,
        });
        setShareUrl(fallbackResponse.data.share_url);
      } catch (fallbackErr) {
        toast.error(err.response?.data?.detail || 'Error creando enlace');
        setShowShareDialog(false);
      }
    } finally {
      setShareLoading(false);
    }
  };
  
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success('Enlace copiado');
    } catch (err) {
      toast.error('Error copiando enlace');
    }
  };
  
  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Mi replay de Clipper',
          text: '¡Mira mi jugada!',
          url: shareUrl,
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          handleCopyLink();
        }
      }
    } else {
      handleCopyLink();
    }
  };
  
  // Force download a video file
  const forceDownload = async (url, filename) => {
    try {
      toast.loading('Descargando video...', { id: 'download' });
      
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error('Error al descargar');
      }
      
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      window.URL.revokeObjectURL(blobUrl);
      toast.success('Video descargado', { id: 'download' });
    } catch (err) {
      console.error('Force download error:', err);
      toast.error('Error al descargar. Intenta abrir el video directamente.', { id: 'download' });
      // Fallback to opening in new tab
      window.open(url, '_blank');
    }
  };

  const handleDownload = async (clip) => {
    if (!canShareOrDownload) {
      // Close detail dialog first to avoid Radix pointer-events conflict
      setSelectedClip(null);
      // Show extend storage modal
      setPendingAction({ type: 'download', clip });
      setShowExtendStorage(true);
      return;
    }

    await downloadClipDirectly(clip);
  };
  
  const downloadClipDirectly = async (clip) => {
    const filename = `clipper_replay_${clip.id.slice(0, 8)}.mp4`;
    
    try {
      const response = await clipsAPI.refreshUrl(clip.id);
      const newUrl = response.data?.download_url;
      
      if (newUrl) {
        if (newUrl.startsWith('/api/clips/download/')) {
          const blobResponse = await clipsAPI.downloadClip(clip.id);
          const blob = new Blob([blobResponse.data], { type: 'video/mp4' });
          const blobUrl = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = blobUrl;
          link.setAttribute('download', filename);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(blobUrl);
          toast.success('Video descargado');
        } else {
          const fullUrl = newUrl.startsWith('/api/') 
            ? `${process.env.REACT_APP_BACKEND_URL || ''}${newUrl}`
            : newUrl;
          await forceDownload(fullUrl, filename);
        }
      } else {
        toast.error('No se pudo obtener el video');
      }
    } catch (err) {
      console.error('Download error:', err);
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      
      if (status === 410) {
        toast.error(detail || 'El video ha expirado. Los videos se almacenan por tiempo limitado.', {
          duration: 5000
        });
      } else if (status === 404) {
        toast.error(detail || 'Video no disponible');
      } else {
        toast.error(detail || 'No se pudo descargar el video');
      }
    }
  };
  
  // Handle email verification complete - retry pending action
  const handleEmailVerified = () => {
    if (pendingAction?.type === 'share' && pendingAction.clip) {
      shareClipDirectly(pendingAction.clip);
    } else if (pendingAction?.type === 'download' && pendingAction.clip) {
      downloadClipDirectly(pendingAction.clip);
    } else if (pendingAction?.type === 'whatsapp' && pendingAction.clip) {
      shareViaWhatsApp(pendingAction.clip);
    }
    setPendingAction(null);
  };
  
  const handlePlayVideo = async (clip) => {
    if (!canPlayInPlatform) {
      // Show complete profile modal
      setPendingAction({ type: 'play', clip });
      setShowCompleteProfile(true);
      return;
    }

    if (clip.status !== 'ready') {
      toast.error('El video aún no está listo');
      return;
    }
    
    await playVideoDirectly(clip);
  };
  
  const playVideoDirectly = async (clip) => {
    setVideoLoading(true);
    setShowVideoPlayer(true);
    setPlayingClip(clip);
    
    try {
      const response = await clipsAPI.getStreamUrl(clip.id);
      const url = response.data?.stream_url;
      
      if (url) {
        setVideoUrl(url);
      } else {
        toast.error('No se pudo cargar el video');
        setShowVideoPlayer(false);
      }
    } catch (err) {
      console.error('Play error:', err);
      toast.error(err.response?.data?.detail || 'Error cargando video');
      setShowVideoPlayer(false);
    } finally {
      setVideoLoading(false);
    }
  };
  
  // Handle profile completion - retry pending action
  const handleProfileComplete = () => {
    if (pendingAction?.type === 'play' && pendingAction.clip) {
      playVideoDirectly(pendingAction.clip);
    }
    setPendingAction(null);
  };
  
  const closeVideoPlayer = () => {
    setShowVideoPlayer(false);
    setVideoUrl('');
    setPlayingClip(null);
  };

  // WhatsApp sharing
  const handleWhatsAppShare = async (clip) => {
    if (!canShareOrDownload) {
      // Close detail dialog first to avoid Radix pointer-events conflict
      setSelectedClip(null);
      setPendingAction({ type: 'whatsapp', clip });
      setShowExtendStorage(true);
      return;
    }

    await shareViaWhatsApp(clip);
  };

  const shareViaWhatsApp = async (clip) => {
    try {
      // First get or create share URL
      let shareUrlToUse = '';
      
      try {
        const response = await clipsAPI.createShortUrl(clip.id);
        shareUrlToUse = response.data.full_url;
      } catch (err) {
        // Fallback to creating a share link
        const fallbackResponse = await shareAPI.createShareLink({
          clip_id: clip.id,
          expires_hours: 72, // 3 days for WhatsApp shares
        });
        shareUrlToUse = fallbackResponse.data.share_url;
      }

      if (!shareUrlToUse) {
        toast.error('No se pudo crear el enlace para compartir');
        return;
      }

      // Format message for WhatsApp
      const clubName = clip.club_name || 'Mi club';
      const courtName = clip.court_name || `Cancha ${clip.court_number || ''}`;
      const message = `🎾 ¡Mira mi replay de ${clubName}!\n📍 ${courtName}\n\n${shareUrlToUse}`;
      
      // Open WhatsApp with pre-filled message
      const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
      window.open(whatsappUrl, '_blank');
      
      toast.success('Abriendo WhatsApp...');
    } catch (err) {
      console.error('WhatsApp share error:', err);
      toast.error('Error al compartir por WhatsApp');
    }
  };
  
  const formatTime = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleTimeString('es-MX', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };
  
  const formatDate = (dateString) => {
    // Parse the date from the group (YYYY-MM-DD in UTC)
    // But we want to show it relative to the user's local date
    const date = new Date(dateString + 'T12:00:00'); // Add noon time to avoid timezone edge cases
    
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    
    if (dateOnly.getTime() === today.getTime()) {
      return 'Hoy';
    } else if (dateOnly.getTime() === yesterday.getTime()) {
      return 'Ayer';
    }
    
    return date.toLocaleDateString('es-MX', {
      weekday: 'long',
      day: 'numeric',
      month: 'short',
    });
  };
  
  return (
    <div className="min-h-screen bg-background">
      {/* Complete Profile Modal */}
      <CompleteProfileModal
        isOpen={showCompleteProfile}
        onClose={() => {
          setShowCompleteProfile(false);
          setPendingAction(null);
        }}
        onComplete={handleProfileComplete}
      />
      
      {/* Extend Storage Modal */}
      <ExtendStorageModal
        isOpen={showExtendStorage}
        onClose={() => {
          setShowExtendStorage(false);
          setPendingAction(null);
        }}
        onVerified={handleEmailVerified}
      />
      
      {/* Navigation */}
      <Navbar />
      
      {/* Unified Stats + Filters bar */}
      {!loading && clips.length > 0 && (
        <div className="border-b border-border bg-card/50">
          <div className="max-w-4xl mx-auto px-4 py-3 space-y-3">
            {/* Stats as clickable filters - with refresh button */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => fetchClips(true)}
                className="flex items-center justify-center p-2.5 rounded-lg bg-secondary/50 hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors"
                title="Actualizar"
                data-testid="refresh-clips"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </button>
              
              <button
                onClick={() => setSelectedType('all')}
                className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg transition-colors ${
                  selectedType === 'all' 
                    ? 'bg-primary/10 text-primary border border-primary/30' 
                    : 'bg-secondary/50 hover:bg-secondary text-foreground'
                }`}
                data-testid="filter-total"
              >
                <Play className="w-4 h-4" />
                <span className="font-bold">{stats.total}</span>
                <span className="text-sm opacity-80">total</span>
              </button>
              
              <button
                onClick={() => setSelectedType('normal')}
                className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg transition-colors ${
                  selectedType === 'normal' 
                    ? 'bg-primary/10 text-primary border border-primary/30' 
                    : 'bg-secondary/50 hover:bg-secondary text-foreground'
                }`}
                data-testid="filter-normal"
              >
                <Clock className="w-4 h-4" />
                <span className="font-bold">{stats.normal}</span>
                <span className="text-sm opacity-80">normal</span>
              </button>
              
              <button
                onClick={() => setSelectedType('fast')}
                className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg transition-colors ${
                  selectedType === 'fast' 
                    ? 'bg-primary/10 text-primary border border-primary/30' 
                    : 'bg-secondary/50 hover:bg-secondary text-foreground'
                }`}
                data-testid="filter-fast"
              >
                <Zap className="w-4 h-4" />
                <span className="font-bold">{stats.fast}</span>
                <span className="text-sm opacity-80">rápido</span>
              </button>
            </div>
            
            {/* Search + Date filters - same row */}
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar por nombre de club..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 h-10 bg-secondary/50"
                />
              </div>
              
              {/* Date filter buttons */}
              <div className="flex items-center gap-1 bg-secondary/50 p-1 rounded-lg shrink-0">
                <Button
                  variant={selectedDate === 'today' ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => setSelectedDate(selectedDate === 'today' ? 'all' : 'today')}
                  className="h-8 px-3 gap-1"
                >
                  <Calendar className="w-3.5 h-3.5" />
                  Hoy
                </Button>
                <Button
                  variant={selectedDate === 'yesterday' ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => setSelectedDate(selectedDate === 'yesterday' ? 'all' : 'yesterday')}
                  className="h-8 px-3 gap-1"
                >
                  <Calendar className="w-3.5 h-3.5" />
                  Ayer
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Content */}
      <div className="max-w-4xl mx-auto p-4">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-primary animate-spin" />
          </div>
        ) : clips.length === 0 ? (
          <div className="text-center py-20">
            <Play className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Sin replays aún</h2>
            <p className="text-muted-foreground mb-6">
              Usa los botones de replay en la cancha para capturar tus jugadas
            </p>
            <Button onClick={() => navigate('/scan')}>
              Ir a escanear cancha
            </Button>
          </div>
        ) : filteredClips.length === 0 ? (
          <div className="text-center py-20">
            <Filter className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Sin resultados</h2>
            <p className="text-muted-foreground mb-6">
              No hay replays que coincidan con tu búsqueda
            </p>
            <Button 
              variant="secondary" 
              onClick={() => {
                setSearchQuery('');
                setSelectedType('all');
                setSelectedDate('all');
              }}
            >
              Mostrar todos
            </Button>
          </div>
        ) : (
          <div className="space-y-6">
            {filteredClips.map((group, groupIndex) => (
              <motion.div
                key={`${group.club_name}-${group.date}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: groupIndex * 0.05 }}
              >
                {/* Group header */}
                <div className="flex items-center gap-3 mb-3">
                  <div className="flex items-center gap-2 text-sm">
                    <MapPin className="w-4 h-4 text-primary" />
                    <span className="font-medium text-foreground">{group.club_name}</span>
                  </div>
                  <div className="h-px flex-1 bg-border" />
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Calendar className="w-4 h-4" />
                    <span>{formatDate(group.date)}</span>
                  </div>
                </div>
                
                {/* Clips grid */}
                <div className="grid gap-3 sm:grid-cols-2">
                  {group.clips.map((clip) => (
                    <motion.div
                      key={clip.id}
                      layoutId={clip.id}
                      className={`clip-card bg-card rounded-xl p-4 border border-border hover:border-primary/30 transition-colors cursor-pointer ${
                        highlightClipId === clip.id ? 'ring-2 ring-primary' : ''
                      }`}
                      onClick={() => handlePlayVideo(clip)}
                      data-testid={`clip-card-${clip.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                            clip.is_fast ? 'bg-primary/10' : 'bg-secondary'
                          }`}>
                            {clip.is_fast ? (
                              <Zap className="w-6 h-6 text-primary" />
                            ) : (
                              <Clock className="w-6 h-6 text-muted-foreground" />
                            )}
                          </div>
                          <div>
                            <p className="font-semibold">
                              {clip.is_fast ? '60s → 30s' : '30s'} {clip.is_fast ? 'Rápido' : 'Normal'}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {formatTime(clip.created_at)}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-1">
                          {clip.status === 'ready' ? (
                            <>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-green-600 hover:text-green-700 hover:bg-green-50"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleWhatsAppShare(clip);
                                }}
                                data-testid={`whatsapp-clip-${clip.id}`}
                                title="Compartir por WhatsApp"
                              >
                                <MessageCircle className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleShare(clip);
                                }}
                                data-testid={`share-clip-${clip.id}`}
                                title="Copiar enlace"
                              >
                                <Share2 className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDownload(clip);
                                }}
                                data-testid={`download-clip-${clip.id}`}
                                title="Descargar video"
                              >
                                <Download className="w-4 h-4" />
                              </Button>
                            </>
                          ) : clip.status === 'retrying' ? (
                            <div className="flex items-center gap-2">
                              <Loader2 className="w-4 h-4 animate-spin text-yellow-500" />
                              <span className="text-xs px-2 py-1 rounded-full bg-yellow-500/10 text-yellow-500">
                                Intento {clip.retry_info?.current || clip.retry_count || 1}/{clip.retry_info?.max || 5}
                              </span>
                            </div>
                          ) : (
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              clip.status === 'processing' 
                                ? 'bg-yellow-500/10 text-yellow-500' 
                                : 'bg-red-500/10 text-red-500'
                            }`}>
                              {clip.status === 'processing' ? 'Procesando...' : 'Error'}
                            </span>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            ))}
            
            {/* Retention notice */}
            <p className="text-xs text-muted-foreground text-center mt-8 pb-4">
              Los replays se eliminan automáticamente después de 48 horas
            </p>
          </div>
        )}
      </div>
      
      {/* Video preview modal */}
      <AnimatePresence>
        {selectedClip && (
          <Dialog open={!!selectedClip} onOpenChange={(open) => {
            if (!open) {
              setSelectedClip(null);
              setVideoUrl('');
            }
          }}>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {selectedClip.is_fast ? (
                    <Zap className="w-5 h-5 text-primary" />
                  ) : (
                    <Clock className="w-5 h-5" />
                  )}
                  Replay {selectedClip.is_fast ? '60s → 30s (2x)' : '30s'}
                </DialogTitle>
                <DialogDescription className="sr-only">
                  Vista previa y opciones para compartir tu replay de {selectedClip.club_name}
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4">
                {/* Video player */}
                <div className="video-container bg-black flex items-center justify-center rounded-xl overflow-hidden min-h-[200px]">
                  {videoLoading ? (
                    <div className="text-center py-8">
                      <Loader2 className="w-8 h-8 text-primary animate-spin mx-auto" />
                      <p className="text-sm text-muted-foreground mt-2">Cargando video...</p>
                    </div>
                  ) : videoUrl && selectedClip.status === 'ready' ? (
                    <video
                      controls
                      autoPlay
                      playsInline
                      crossOrigin="anonymous"
                      className="w-full h-full object-contain"
                      src={videoUrl}
                      style={{ maxHeight: '300px' }}
                      onLoadedMetadata={(e) => {
                        if (selectedClip.is_fast) {
                          e.target.playbackRate = 2.0;
                        }
                      }}
                      onError={(e) => {
                        // Only retry once by checking if we already removed crossOrigin
                        if (e.target.crossOrigin !== null) {
                          console.log('Video error, trying without crossOrigin');
                          e.target.crossOrigin = null;
                          e.target.load();
                        } else {
                          console.error('Video failed to load');
                          toast.error('No se pudo cargar el video');
                        }
                      }}
                    >
                      Tu navegador no soporta video HTML5
                    </video>
                  ) : selectedClip.status === 'ready' ? (
                    <div className="text-center py-8 px-4">
                      <Play className="w-12 h-12 text-primary mx-auto mb-3" />
                      <p className="text-sm text-muted-foreground mb-4">
                        El video está listo
                      </p>
                      <Button 
                        size="sm" 
                        onClick={() => handleDownload(selectedClip)}
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Ver video
                      </Button>
                    </div>
                  ) : selectedClip.status === 'retrying' ? (
                    <div className="text-center py-8">
                      <Loader2 className="w-8 h-8 text-yellow-500 animate-spin mx-auto" />
                      <p className="text-sm text-yellow-500 mt-2">
                        Reintentando ({selectedClip.retry_info?.current || selectedClip.retry_count || 1}/{selectedClip.retry_info?.max || 5})
                      </p>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Loader2 className="w-8 h-8 text-primary animate-spin mx-auto" />
                      <p className="text-sm text-muted-foreground mt-2">Procesando...</p>
                    </div>
                  )}
                </div>
                
                {/* Speed indicator for fast clips */}
                {selectedClip.is_fast && selectedClip.status === 'ready' && videoUrl && (
                  <div className="text-center text-sm text-primary bg-primary/10 rounded-lg py-2">
                    ⚡ Reproduciendo a velocidad 2x
                  </div>
                )}
                
                {/* Actions - responsive grid for mobile */}
                <div className="grid grid-cols-3 gap-2">
                  <Button
                    className="flex-1 px-2 sm:px-4"
                    onClick={() => handleDownload(selectedClip)}
                    disabled={selectedClip.status !== 'ready'}
                    data-testid="modal-download-btn"
                  >
                    <Download className="w-4 h-4 sm:mr-2" />
                    <span className="hidden sm:inline">Descargar</span>
                  </Button>
                  <Button
                    variant="secondary"
                    className="flex-1 px-2 sm:px-4"
                    onClick={() => handleShare(selectedClip)}
                    disabled={selectedClip.status !== 'ready'}
                    data-testid="modal-share-btn"
                  >
                    <Share2 className="w-4 h-4 sm:mr-2" />
                    <span className="hidden sm:inline">Enlace</span>
                  </Button>
                  <Button
                    variant="outline"
                    className="flex-1 px-2 sm:px-4 text-green-600 border-green-600 hover:bg-green-50 hover:text-green-700"
                    onClick={() => handleWhatsAppShare(selectedClip)}
                    disabled={selectedClip.status !== 'ready'}
                    data-testid="modal-whatsapp-btn"
                  >
                    <MessageCircle className="w-4 h-4 sm:mr-2" />
                    <span className="hidden sm:inline">WhatsApp</span>
                  </Button>
                </div>
                
                {/* Info */}
                <div className="text-sm text-muted-foreground space-y-1 bg-secondary/50 rounded-lg p-3">
                  <p><span className="text-foreground">Club:</span> {selectedClip.club_name}</p>
                  <p><span className="text-foreground">Creado:</span> {new Date(selectedClip.created_at).toLocaleString('es-MX')}</p>
                  {selectedClip.expires_at && (
                    <p><span className="text-foreground">Expira:</span> {new Date(selectedClip.expires_at).toLocaleString('es-MX')}</p>
                  )}
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </AnimatePresence>
      
      {/* Share dialog */}
      <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Compartir replay</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {shareLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-8 h-8 text-primary animate-spin" />
              </div>
            ) : (
              <>
                <p className="text-sm text-muted-foreground">
                  Este enlace expira en 24 horas
                </p>
                
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={shareUrl}
                    readOnly
                    className="flex-1 bg-secondary rounded-lg px-3 py-2 text-sm truncate"
                  />
                  <Button
                    size="icon"
                    variant="secondary"
                    onClick={handleCopyLink}
                  >
                    {copied ? (
                      <Check className="w-4 h-4 text-primary" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                  </Button>
                </div>
                
                <Button
                  className="w-full"
                  onClick={handleNativeShare}
                >
                  <Share2 className="w-4 h-4 mr-2" />
                  Compartir
                </Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Fullscreen Video Player Modal */}
      <AnimatePresence>
        {showVideoPlayer && playingClip && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/95 flex flex-col"
            data-testid="video-player-modal"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 text-white">
              <div className="flex items-center gap-3">
                {playingClip.is_fast ? (
                  <div className="flex items-center gap-2 text-primary">
                    <Zap className="w-5 h-5" />
                    <span className="font-medium">60s → 30s (2x)</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Clock className="w-5 h-5" />
                    <span className="font-medium">30s Normal</span>
                  </div>
                )}
                <span className="text-sm text-white/60">•</span>
                <span className="text-sm text-white/60">{playingClip.club_name}</span>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="text-white hover:bg-white/10"
                onClick={closeVideoPlayer}
                data-testid="close-video-player"
              >
                <X className="w-6 h-6" />
              </Button>
            </div>
            
            {/* Video Container */}
            <div className="flex-1 flex items-center justify-center px-4 pb-4">
              {videoLoading ? (
                <div className="text-center">
                  <Loader2 className="w-12 h-12 text-primary animate-spin mx-auto" />
                  <p className="text-white/60 mt-4">Cargando video...</p>
                </div>
              ) : videoUrl ? (
                <video
                  controls
                  autoPlay
                  playsInline
                  crossOrigin="anonymous"
                  className="max-w-full max-h-full rounded-xl"
                  src={videoUrl}
                  style={{ maxHeight: 'calc(100vh - 180px)' }}
                  onLoadedMetadata={(e) => {
                    if (playingClip.is_fast) {
                      e.target.playbackRate = 2.0;
                    }
                  }}
                  onError={(e) => {
                    if (e.target.crossOrigin !== null) {
                      console.log('Video error, trying without crossOrigin');
                      e.target.crossOrigin = null;
                      e.target.load();
                    } else {
                      toast.error('No se pudo cargar el video');
                    }
                  }}
                  data-testid="video-player-element"
                >
                  Tu navegador no soporta video HTML5
                </video>
              ) : (
                <div className="text-center text-white/60">
                  <Play className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <p>No se pudo cargar el video</p>
                </div>
              )}
            </div>
            
            {/* Actions Bar */}
            <div className="p-4 bg-black/50 backdrop-blur-sm">
              <div className="max-w-lg mx-auto grid grid-cols-3 gap-2 px-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="border-white/20 text-white hover:bg-white/10 min-w-0"
                  onClick={() => {
                    closeVideoPlayer();
                    handleDownload(playingClip);
                  }}
                  data-testid="video-player-download"
                >
                  <Download className="w-4 h-4 sm:mr-2 shrink-0" />
                  <span className="hidden sm:inline truncate">Descargar</span>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="border-white/20 text-white hover:bg-white/10 min-w-0"
                  onClick={() => {
                    closeVideoPlayer();
                    handleShare(playingClip);
                  }}
                  data-testid="video-player-share"
                >
                  <Share2 className="w-4 h-4 sm:mr-2 shrink-0" />
                  <span className="hidden sm:inline truncate">Compartir</span>
                </Button>
                <Button
                  size="sm"
                  className="bg-green-600 hover:bg-green-700 text-white min-w-0"
                  onClick={() => {
                    closeVideoPlayer();
                    handleWhatsAppShare(playingClip);
                  }}
                  data-testid="video-player-whatsapp"
                >
                  <MessageCircle className="w-4 h-4 sm:mr-2 shrink-0" />
                  <span className="hidden sm:inline truncate">WhatsApp</span>
                </Button>
              </div>
              
              {/* Speed indicator */}
              {playingClip.is_fast && (
                <p className="text-center text-sm text-primary mt-3">
                  ⚡ Reproduciendo a velocidad 2x
                </p>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
